import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, FileText, Palette } from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";
import { useWorkspace } from "@/hooks/useWorkspace";

const typeConfig = {
  client: { label: "Clientes", icon: Users },
  demand: { label: "Demandas", icon: FileText },
  content: { label: "Conteúdos", icon: Palette },
};

export function GlobalSearchCommand() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { results, enabled } = useGlobalSearch(query);
  const { currentWorkspace } = useWorkspace();
  const isClientWorkspace = currentWorkspace?.role === "client";
  const placeholder = isClientWorkspace
    ? "Buscar conteúdo, demandas..."
    : "Buscar clientes, demandas, conteúdo...";

  // Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = useCallback(
    (route: string) => {
      setOpen(false);
      setQuery("");
      navigate(route);
    },
    [navigate]
  );

  const grouped = Object.entries(typeConfig).reduce<
    Record<string, typeof results>
  >((acc, [key]) => {
    acc[key] = results.filter((r) => r.type === key);
    return acc;
  }, {});

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative w-80 hidden md:flex items-center gap-2 rounded-md border border-input bg-secondary px-3 py-2 text-sm text-muted-foreground hover:bg-accent/50 transition-colors"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">{placeholder}</span>
        <kbd className="pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          ⌘K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
        <CommandInput
          placeholder={placeholder}
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {enabled && results.length === 0 && (
            <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
          )}
          {!enabled && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Digite pelo menos 2 caracteres para buscar...
            </div>
          )}
          {Object.entries(grouped).map(([type, items]) => {
            if (!items.length) return null;
            const config = typeConfig[type as keyof typeof typeConfig];
            const Icon = config.icon;
            return (
              <CommandGroup key={type} heading={config.label}>
                {items.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={`${item.title} ${item.subtitle ?? ""}`}
                    onSelect={() => handleSelect(item.route)}
                  >
                    <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    <div className="flex flex-col">
                      <span>{item.title}</span>
                      {item.subtitle && (
                        <span className="text-xs text-muted-foreground">
                          {item.subtitle}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}
        </CommandList>
      </CommandDialog>
    </>
  );
}
